import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Thermometer, ShieldCheck, AlertTriangle, Package, ClipboardList, Settings, FileDown, Plus, Trash2, CheckCircle, XCircle, Camera, Clock, Loader2, BookOpen, ChefHat, Droplets, Sparkles, Trophy, Award, Upload } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function HygienePage() {
  const { api, user: authUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [relevesF, setRelevesF] = useState([]);
  const [relevesP, setRelevesP] = useState([]);
  const [receptions, setReceptions] = useState([]);
  const [dlcItems, setDlcItems] = useState([]);
  const [productions, setProductions] = useState([]);
  const [huiles, setHuiles] = useState([]);
  const [etatNettoyage, setEtatNettoyage] = useState([]);
  const [execNettoyage, setExecNettoyage] = useState([]);
  const [audit, setAudit] = useState(null);
  const [qcmList, setQcmList] = useState([]);
  const [resultats, setResultats] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [mercuriale, setMercuriale] = useState([]);
  const [dialogType, setDialogType] = useState(null);
  const [exporting, setExporting] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const nowTime = new Date().toTimeString().slice(0, 5);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cR, dR, rfR, rpR, recR, dlcR, prR, hR, nR, exR, qR, resR, eR, mR] = await Promise.all([
        api.get('/hygiene/config'),
        api.get('/hygiene/dashboard'),
        api.get('/hygiene/releves-frigo'),
        api.get('/hygiene/releves-produit'),
        api.get('/hygiene/receptions'),
        api.get('/hygiene/dlc'),
        api.get('/hygiene/productions'),
        api.get('/hygiene/huiles'),
        api.get('/hygiene/nettoyage/etat'),
        api.get('/hygiene/nettoyage/executions'),
        api.get('/hygiene/formation/qcm'),
        api.get('/hygiene/formation/resultats'),
        api.get('/decongelation/emplacements'),
        api.get('/mercuriale/produits'),
      ]);
      setConfig(cR.data); setDashboard(dR.data);
      setRelevesF(rfR.data); setRelevesP(rpR.data);
      setReceptions(recR.data); setDlcItems(dlcR.data);
      setProductions(prR.data); setHuiles(hR.data);
      setEtatNettoyage(nR.data); setExecNettoyage(exR.data);
      setQcmList(qR.data); setResultats(resR.data);
      setEmplacements(eR.data);
      setMercuriale(mR.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/api/hygiene/export-pdf?token=${token}`, '_blank');
    } catch { toast.error('Erreur export'); }
    finally { setExporting(false); }
  };

  const d = dashboard || {};
  const c = config || {};

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="hygiene-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-green-600" /> Hygiene & HACCP</h1>
          <p className="text-muted-foreground text-sm">Temperatures, tracabilite, receptions, production — PMS complet</p>
        </div>
        <Button variant="outline" onClick={handleExport} disabled={exporting} data-testid="export-pdf-btn">
          <FileDown className="w-4 h-4 mr-2" /> {exporting ? 'Export...' : 'Export PDF Controle'}
        </Button>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <DashCard icon={Thermometer} label="Releves frigo" value={d.releves_frigo_today || 0} sub="aujourd'hui" color="text-blue-600" alert={d.non_conformes_frigo > 0} alertText={`${d.non_conformes_frigo} NC`} />
        <DashCard icon={Thermometer} label="Releves produit" value={d.releves_produit_today || 0} sub="aujourd'hui" color="text-purple-600" alert={d.non_conformes_produit > 0} alertText={`${d.non_conformes_produit} NC`} />
        <DashCard icon={Package} label="Receptions" value={d.receptions_today || 0} sub="aujourd'hui" color="text-green-600" />
        <DashCard icon={AlertTriangle} label="DLC" value={d.dlc_expirees || 0} sub={`expirees + ${d.dlc_alerte || 0} alertes`} color={d.dlc_expirees > 0 ? 'text-red-600' : 'text-green-600'} alert={d.dlc_expirees > 0} />
        <DashCard icon={Droplets} label="Huiles" value={(d.huiles_alerte || []).length} sub={(d.huiles_alerte || []).length > 0 ? `a changer: ${(d.huiles_alerte || []).join(', ')}` : 'toutes OK'} color={(d.huiles_alerte || []).length > 0 ? 'text-amber-600' : 'text-green-600'} alert={(d.huiles_alerte || []).length > 0} alertText={d.huile_non_conformes_today > 0 ? `${d.huile_non_conformes_today} NC` : null} />
      </div>

      <Tabs defaultValue="temperatures">
        <TabsList className="flex-wrap">
          <TabsTrigger value="temperatures" data-testid="tab-temp"><Thermometer className="w-4 h-4 mr-1" /> Temperatures</TabsTrigger>
          <TabsTrigger value="reception" data-testid="tab-recep"><Package className="w-4 h-4 mr-1" /> Reception</TabsTrigger>
          <TabsTrigger value="dlc" data-testid="tab-dlc"><Clock className="w-4 h-4 mr-1" /> DLC</TabsTrigger>
          <TabsTrigger value="production" data-testid="tab-prod"><ChefHat className="w-4 h-4 mr-1" /> Production</TabsTrigger>
          <TabsTrigger value="huiles" data-testid="tab-huiles"><Droplets className="w-4 h-4 mr-1" /> Huiles</TabsTrigger>
          <TabsTrigger value="nettoyage" data-testid="tab-nettoyage"><Sparkles className="w-4 h-4 mr-1" /> Nettoyage</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><Trophy className="w-4 h-4 mr-1" /> Audit</TabsTrigger>
          <TabsTrigger value="formation" data-testid="tab-formation"><Award className="w-4 h-4 mr-1" /> Formation</TabsTrigger>
          <TabsTrigger value="regles" data-testid="tab-regles"><BookOpen className="w-4 h-4 mr-1" /> Regles HACCP</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config-hyg"><Settings className="w-4 h-4 mr-1" /> Config</TabsTrigger>
        </TabsList>

        {/* ===== TEMPERATURES ===== */}
        <TabsContent value="temperatures" className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={() => setDialogType('releve_frigo')} data-testid="add-releve-frigo"><Plus className="w-4 h-4 mr-1" /> Releve Frigo</Button>
            <Button variant="outline" onClick={() => setDialogType('releve_produit')} data-testid="add-releve-produit"><Plus className="w-4 h-4 mr-1" /> Releve Produit</Button>
          </div>
          {/* Frigo readings */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Releves Frigos</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Heure</TableHead><TableHead>Moment</TableHead>
                  <TableHead>Emplacement</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Temp.</TableHead>
                  <TableHead>Conforme</TableHead><TableHead>Par</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {relevesF.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-muted-foreground">Aucun releve</TableCell></TableRow> :
                    relevesF.slice(0, 20).map(r => (
                      <TableRow key={r.id} className={!r.conforme ? 'bg-red-50/50' : ''}>
                        <TableCell>{r.date}</TableCell><TableCell>{r.heure}</TableCell>
                        <TableCell><Badge variant="outline">{r.moment}</Badge></TableCell>
                        <TableCell>{r.emplacement}</TableCell>
                        <TableCell>
                          {r.type_emplacement === 'congelateur' ? <Badge className="bg-blue-100 text-blue-700">Congélateur</Badge> :
                           r.type_emplacement === 'saladette' ? <Badge className="bg-green-100 text-green-700">Saladette</Badge> :
                           <Badge className="bg-amber-100 text-amber-700">Frigo positif</Badge>}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${r.conforme ? 'text-green-600' : 'text-red-600'}`}>{r.temperature}°C</TableCell>
                        <TableCell>{r.conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                        <TableCell className="text-xs">{r.utilisateur}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {/* Product readings */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Releves Produits</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Produit</TableHead><TableHead>Emplacement</TableHead>
                  <TableHead className="text-right">Temp.</TableHead><TableHead className="text-right">Max</TableHead>
                  <TableHead>Conforme</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {relevesP.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Aucun releve</TableCell></TableRow> :
                    relevesP.slice(0, 20).map(r => (
                      <TableRow key={r.id} className={!r.conforme ? 'bg-red-50/50' : ''}>
                        <TableCell>{r.date} {r.heure}</TableCell><TableCell className="font-medium">{r.produit}</TableCell>
                        <TableCell>{r.emplacement}</TableCell>
                        <TableCell className={`text-right font-bold ${r.conforme ? 'text-green-600' : 'text-red-600'}`}>{r.temperature}°C</TableCell>
                        <TableCell className="text-right text-muted-foreground">{r.temp_max}°C</TableCell>
                        <TableCell>{r.conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== RECEPTION ===== */}
        <TabsContent value="reception" className="space-y-4">
          <Button onClick={() => setDialogType('reception')} data-testid="add-reception"><Plus className="w-4 h-4 mr-1" /> Nouvelle reception</Button>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Fournisseur</TableHead><TableHead>Lot</TableHead>
                <TableHead>Temp.</TableHead><TableHead>Emballage</TableHead><TableHead>DLC</TableHead>
                <TableHead>Aspect</TableHead><TableHead>Global</TableHead><TableHead>Par</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {receptions.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground">Aucune reception</TableCell></TableRow> :
                  receptions.slice(0, 20).map(r => (
                    <TableRow key={r.id} className={!r.conforme_global ? 'bg-red-50/50' : ''}>
                      <TableCell>{r.date}</TableCell><TableCell className="font-medium">{r.fournisseur}</TableCell>
                      <TableCell className="font-mono text-sm">{r.numero_lot || '-'}</TableCell>
                      <TableCell>{r.temperature != null ? `${r.temperature}°C` : '-'}</TableCell>
                      <TableCell>{r.emballage_conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                      <TableCell>{r.dlc_conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                      <TableCell>{r.aspect_conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                      <TableCell>{r.conforme_global ? <Badge className="bg-green-100 text-green-700">OK</Badge> : <Badge className="bg-red-100 text-red-700">NC</Badge>}</TableCell>
                      <TableCell className="text-xs">{r.utilisateur}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== DLC ===== */}
        <TabsContent value="dlc" className="space-y-4">
          <Button onClick={() => setDialogType('dlc')} data-testid="add-dlc"><Plus className="w-4 h-4 mr-1" /> Ajouter un produit</Button>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Statut</TableHead><TableHead>Produit</TableHead><TableHead>Lot</TableHead>
                <TableHead>DLC</TableHead><TableHead>Quantite</TableHead><TableHead>Emplacement</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {dlcItems.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">Aucun suivi DLC</TableCell></TableRow> :
                  dlcItems.map(d => (
                    <TableRow key={d.id} className={d.statut_dlc === 'expiree' ? 'bg-red-50/50' : d.statut_dlc === 'alerte' ? 'bg-amber-50/50' : ''}>
                      <TableCell>
                        {d.statut_dlc === 'expiree' ? <Badge className="bg-red-500 text-white">Expiree</Badge> :
                         d.statut_dlc === 'alerte' ? <Badge className="bg-amber-100 text-amber-700">Alerte</Badge> :
                         <Badge className="bg-green-100 text-green-700">OK</Badge>}
                      </TableCell>
                      <TableCell className="font-medium">{d.produit}</TableCell>
                      <TableCell className="font-mono text-sm">{d.numero_lot || '-'}</TableCell>
                      <TableCell className="font-medium">{d.dlc}</TableCell>
                      <TableCell>{d.quantite || '-'}</TableCell>
                      <TableCell>{d.emplacement || '-'}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => {
                          try { await api.delete(`/hygiene/dlc/${d.id}`); toast.success('Supprime'); fetchAll(); } catch { toast.error('Erreur'); }
                        }}><Trash2 className="w-3 h-3" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== PRODUCTION ===== */}
        <TabsContent value="production" className="space-y-4">
          <Button onClick={() => setDialogType('production')} data-testid="add-production"><Plus className="w-4 h-4 mr-1" /> Nouvelle production</Button>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Plat</TableHead><TableHead>Quantite</TableHead>
                <TableHead>Temp. finale</TableHead><TableHead>DLC</TableHead><TableHead>Par</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {productions.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Aucune production</TableCell></TableRow> :
                  productions.slice(0, 20).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.date} {p.heure}</TableCell><TableCell className="font-medium">{p.plat}</TableCell>
                      <TableCell>{p.quantite_produite || '-'}</TableCell>
                      <TableCell>{p.temperature_finale != null ? `${p.temperature_finale}°C` : '-'}</TableCell>
                      <TableCell>{p.dlc_produit || '-'}</TableCell>
                      <TableCell className="text-xs">{p.utilisateur}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== HUILES ===== */}
        <TabsContent value="huiles" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={() => setDialogType('huile')} data-testid="add-huile"><Plus className="w-4 h-4 mr-1" /> Nouveau controle</Button>
            {(d.huiles_alerte || []).length > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                <AlertTriangle className="w-3 h-3 mr-1" /> A changer: {(d.huiles_alerte || []).join(', ')}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground ml-auto">Seuil polarite: {c.huile_polarite_max || 24}% — Frequence changement: {c.huile_changement_jours || 7}j</span>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Bac</TableHead><TableHead>Action</TableHead>
                <TableHead className="text-right">Polarite</TableHead><TableHead>Aspect</TableHead>
                <TableHead>Couleur</TableHead><TableHead>Conforme</TableHead><TableHead>Par</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {huiles.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-6 text-muted-foreground">Aucun controle</TableCell></TableRow> :
                  huiles.slice(0, 30).map(h => (
                    <TableRow key={h.id} className={!h.conforme ? 'bg-red-50/50' : ''}>
                      <TableCell>{h.date} {h.heure}</TableCell>
                      <TableCell className="font-medium">{h.bac}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          h.action === 'changement' ? 'border-green-500 text-green-700' :
                          h.action === 'filtrage' ? 'border-blue-500 text-blue-700' :
                          h.action === 'appoint' ? 'border-amber-500 text-amber-700' : ''
                        }>{h.action}</Badge>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${h.conforme ? 'text-green-600' : 'text-red-600'}`}>
                        {h.polarite != null ? `${h.polarite}%` : '-'}
                      </TableCell>
                      <TableCell className="text-sm">{h.aspect || '-'}</TableCell>
                      <TableCell className="text-sm">{h.couleur || '-'}</TableCell>
                      <TableCell>{h.conforme ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}</TableCell>
                      <TableCell className="text-xs">{h.utilisateur}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={async () => {
                          try { await api.delete(`/hygiene/huiles/${h.id}`); toast.success('Supprime'); fetchAll(); } catch { toast.error('Erreur'); }
                        }}><Trash2 className="w-3 h-3" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ===== NETTOYAGE ===== */}
        <TabsContent value="nettoyage" className="space-y-4">
          <NettoyageTab etat={etatNettoyage} executions={execNettoyage} config={c} api={api} onRefresh={fetchAll} />
        </TabsContent>

        {/* ===== AUDIT BLANC ===== */}
        <TabsContent value="audit" className="space-y-4">
          <AuditTab api={api} />
        </TabsContent>

        {/* ===== FORMATION ===== */}
        <TabsContent value="formation" className="space-y-4">
          <FormationTab api={api} config={c} qcmList={qcmList} resultats={resultats} onRefresh={fetchAll} />
        </TabsContent>

        {/* ===== REGLES HACCP ===== */}
        <TabsContent value="regles" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-5 h-5" /> Regles d'Hygiene HACCP</CardTitle>
              <CardDescription>Regles parametrables dans l'onglet Configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(c.regles_haccp || []).map((r, i) => (
                <div key={i} className="p-3 rounded-lg border bg-muted/20">
                  <h4 className="font-semibold text-sm flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-600" /> {r.titre}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{r.desc}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CONFIG ===== */}
        <TabsContent value="config" className="space-y-4">
          <ConfigPanel config={c} api={api} onSave={fetchAll} />
        </TabsContent>
      </Tabs>

      {/* ===== DIALOGS ===== */}
      <ReleveFrigoDialog open={dialogType === 'releve_frigo'} onClose={() => setDialogType(null)} api={api} emplacements={emplacements} today={today} nowTime={nowTime} onSuccess={fetchAll} />
      <ReleveProduitDialog open={dialogType === 'releve_produit'} onClose={() => setDialogType(null)} api={api} emplacements={emplacements} config={c} mercuriale={mercuriale} today={today} nowTime={nowTime} onSuccess={fetchAll} />
      <ReceptionDialog open={dialogType === 'reception'} onClose={() => setDialogType(null)} api={api} mercuriale={mercuriale} today={today} nowTime={nowTime} onSuccess={fetchAll} />
      <DlcDialog open={dialogType === 'dlc'} onClose={() => setDialogType(null)} api={api} emplacements={emplacements} mercuriale={mercuriale} today={today} onSuccess={fetchAll} />
      <ProductionDialog open={dialogType === 'production'} onClose={() => setDialogType(null)} api={api} mercuriale={mercuriale} today={today} nowTime={nowTime} onSuccess={fetchAll} />
      <HuileDialog open={dialogType === 'huile'} onClose={() => setDialogType(null)} api={api} config={c} today={today} nowTime={nowTime} onSuccess={fetchAll} />
    </div>
  );
}

// ===== DASHBOARD CARD =====
function DashCard({ icon: Icon, label, value, sub, color, alert, alertText }) {
  return (
    <Card className={alert ? 'border-red-300 bg-red-50/30' : ''}>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className="flex items-end gap-2">
          <p className={`text-2xl font-bold tabular-nums ${color}`}>{value}</p>
          {alert && alertText && <Badge className="bg-red-500 text-white text-[10px]">{alertText}</Badge>}
        </div>
        <p className="text-[10px] text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

// ===== SCAN OCR BUTTON (reusable) =====
function ScanOCRButton({ api, onDetected }) {
  const [scanning, setScanning] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/hygiene/scan-temperature', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.temperature != null) {
        onDetected(res.data.temperature, res.data);
        toast.success(`Detecte: ${res.data.temperature}°C (${res.data.confiance})`);
      } else {
        toast.error('Temperature non detectee. Saisie manuelle.');
      }
    } catch (err) {
      toast.error('Erreur scan');
    } finally {
      setScanning(false);
      e.target.value = '';
    }
  };
  return (
    <label className="cursor-pointer">
      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="scan-ocr-input" />
      <Button type="button" variant="outline" size="sm" disabled={scanning} asChild>
        <span>{scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />}{scanning ? 'Scan...' : 'Scan photo'}</span>
      </Button>
    </label>
  );
}

// ===== RELEVE FRIGO DIALOG =====
function ReleveFrigoDialog({ open, onClose, api, emplacements, today, nowTime, onSuccess }) {
  const [form, setForm] = useState({ date: today, heure: nowTime, moment: 'matin', emplacement: '', temperature: '' });
  const reset = () => setForm({ date: today, heure: nowTime, moment: 'matin', emplacement: '', temperature: '' });
  const selected = emplacements.find(e => e.nom === form.emplacement);
  const typeLabel = { frigo_positif: 'Frigo positif (0-4°C)', saladette: 'Saladette (0-4°C)', congelateur: 'Congélateur (-25 à -18°C)' };
  const byType = emplacements.reduce((acc, e) => {
    const t = e.type || 'frigo_positif';
    (acc[t] = acc[t] || []).push(e);
    return acc;
  }, {});
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent data-testid="dialog-releve-frigo">
        <DialogHeader><DialogTitle>Releve Temperature Frigo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Heure</Label><Input type="time" value={form.heure} onChange={e => setForm(f => ({...f, heure: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Moment</Label>
              <Select value={form.moment} onValueChange={v => setForm(f => ({...f, moment: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="matin">Matin</SelectItem><SelectItem value="midi">Midi</SelectItem><SelectItem value="soir">Soir</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Emplacement</Label>
              <Select value={form.emplacement} onValueChange={v => setForm(f => ({...f, emplacement: v}))}>
                <SelectTrigger data-testid="emplacement-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {['frigo_positif', 'saladette', 'congelateur'].map(t => (byType[t] || []).length > 0 && (
                    <div key={t}>
                      <div className="px-2 py-1 text-[10px] uppercase text-muted-foreground font-semibold">{typeLabel[t]}</div>
                      {byType[t].map(e => <SelectItem key={e.id} value={e.nom}>{e.nom}</SelectItem>)}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {selected && <p className="text-[10px] text-muted-foreground mt-1">Type: <b>{typeLabel[selected.type || 'frigo_positif']}</b></p>}
            </div>
            <div className="space-y-1"><Label>Temperature (°C)</Label>
              <div className="flex gap-2">
                <Input type="number" step="0.1" value={form.temperature} onChange={e => setForm(f => ({...f, temperature: e.target.value}))} placeholder="3.2" data-testid="temp-frigo-input" />
                <ScanOCRButton api={api} onDetected={(t) => setForm(f => ({...f, temperature: String(t)}))} />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.emplacement || form.temperature === '') { toast.error('Emplacement et temperature requis'); return; }
            try {
              const payload = {
                ...form,
                temperature: parseFloat(form.temperature),
                type_emplacement: selected?.type || undefined,
              };
              await api.post('/hygiene/releves-frigo', payload);
              toast.success('Releve enregistre');
              onSuccess(); onClose(); reset();
            } catch { toast.error('Erreur'); }
          }} data-testid="save-releve-frigo">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== RELEVE PRODUIT DIALOG =====
function ReleveProduitDialog({ open, onClose, api, emplacements, config, mercuriale, today, nowTime, onSuccess }) {
  const [form, setForm] = useState({ date: today, heure: nowTime, produit: '', emplacement: '', temperature: '' });
  const produits = config?.produits_a_suivre || [];
  const reset = () => setForm({ date: today, heure: nowTime, produit: '', emplacement: '', temperature: '' });
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent data-testid="dialog-releve-produit">
        <DialogHeader><DialogTitle>Releve Temperature Produit</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Heure</Label><Input type="time" value={form.heure} onChange={e => setForm(f => ({...f, heure: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Produit</Label>
              <Select value={form.produit} onValueChange={v => setForm(f => ({...f, produit: v}))}>
                <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {produits.map((p, i) => <SelectItem key={i} value={p.nom}>{p.nom} (max {p.temp_max}°C)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Emplacement</Label>
              <Select value={form.emplacement} onValueChange={v => setForm(f => ({...f, emplacement: v}))}>
                <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{emplacements.map(e => <SelectItem key={e.id} value={e.nom}>{e.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label>Temperature (°C)</Label>
            <div className="flex gap-2">
              <Input type="number" step="0.1" value={form.temperature} onChange={e => setForm(f => ({...f, temperature: e.target.value}))} placeholder="3.5" />
              <ScanOCRButton api={api} onDetected={(t) => setForm(f => ({...f, temperature: String(t)}))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.produit || form.temperature === '') { toast.error('Produit et temperature requis'); return; }
            try { await api.post('/hygiene/releves-produit', { ...form, temperature: parseFloat(form.temperature) }); toast.success('Releve enregistre'); onSuccess(); onClose(); reset(); }
            catch { toast.error('Erreur'); }
          }}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== RECEPTION DIALOG =====
function ReceptionDialog({ open, onClose, api, mercuriale, today, nowTime, onSuccess }) {
  const [form, setForm] = useState({ date: today, heure: nowTime, fournisseur: '', numero_lot: '', temperature: '', emballage_conforme: true, dlc_conforme: true, aspect_conforme: true, dlc: '', notes: '' });
  const reset = () => setForm({ date: today, heure: nowTime, fournisseur: '', numero_lot: '', temperature: '', emballage_conforme: true, dlc_conforme: true, aspect_conforme: true, dlc: '', notes: '' });
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent className="max-w-lg" data-testid="dialog-reception">
        <DialogHeader><DialogTitle>Reception Marchandises</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Heure</Label><Input type="time" value={form.heure} onChange={e => setForm(f => ({...f, heure: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Fournisseur</Label><Input value={form.fournisseur} onChange={e => setForm(f => ({...f, fournisseur: e.target.value}))} placeholder="Metro, Promocash..." /></div>
            <div className="space-y-1"><Label>N° Lot</Label><Input value={form.numero_lot} onChange={e => setForm(f => ({...f, numero_lot: e.target.value}))} className="font-mono" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Temperature (°C)</Label><Input type="number" step="0.1" value={form.temperature} onChange={e => setForm(f => ({...f, temperature: e.target.value}))} placeholder="3.0" /></div>
            <div className="space-y-1"><Label>DLC</Label><Input type="date" value={form.dlc} onChange={e => setForm(f => ({...f, dlc: e.target.value}))} /></div>
          </div>
          <Separator />
          <div className="space-y-2">
            <Label className="font-semibold">Controles conformite</Label>
            <div className="flex items-center justify-between"><span className="text-sm">Emballage intact</span><Switch checked={form.emballage_conforme} onCheckedChange={v => setForm(f => ({...f, emballage_conforme: v}))} /></div>
            <div className="flex items-center justify-between"><span className="text-sm">DLC conforme</span><Switch checked={form.dlc_conforme} onCheckedChange={v => setForm(f => ({...f, dlc_conforme: v}))} /></div>
            <div className="flex items-center justify-between"><span className="text-sm">Aspect visuel OK</span><Switch checked={form.aspect_conforme} onCheckedChange={v => setForm(f => ({...f, aspect_conforme: v}))} /></div>
          </div>
          <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Observations..." /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.fournisseur) { toast.error('Fournisseur requis'); return; }
            try { const payload = { ...form, temperature: form.temperature ? parseFloat(form.temperature) : null }; await api.post('/hygiene/receptions', payload); toast.success('Reception enregistree'); onSuccess(); onClose(); reset(); }
            catch { toast.error('Erreur'); }
          }}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== DLC DIALOG =====
function DlcDialog({ open, onClose, api, emplacements, mercuriale, today, onSuccess }) {
  const [form, setForm] = useState({ produit: '', numero_lot: '', fournisseur: '', dlc: '', quantite: '', emplacement: '' });
  const reset = () => setForm({ produit: '', numero_lot: '', fournisseur: '', dlc: '', quantite: '', emplacement: '' });
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent data-testid="dialog-dlc">
        <DialogHeader><DialogTitle>Suivi DLC</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Produit</Label><Input value={form.produit} onChange={e => setForm(f => ({...f, produit: e.target.value}))} placeholder="Ex: Saumon frais" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>N° Lot</Label><Input value={form.numero_lot} onChange={e => setForm(f => ({...f, numero_lot: e.target.value}))} className="font-mono" /></div>
            <div className="space-y-1"><Label>Fournisseur</Label><Input value={form.fournisseur} onChange={e => setForm(f => ({...f, fournisseur: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>DLC</Label><Input type="date" value={form.dlc} onChange={e => setForm(f => ({...f, dlc: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Quantite</Label><Input value={form.quantite} onChange={e => setForm(f => ({...f, quantite: e.target.value}))} placeholder="2 kg" /></div>
            <div className="space-y-1"><Label>Emplacement</Label>
              <Select value={form.emplacement} onValueChange={v => setForm(f => ({...f, emplacement: v}))}>
                <SelectTrigger><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{emplacements.map(e => <SelectItem key={e.id} value={e.nom}>{e.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.produit || !form.dlc) { toast.error('Produit et DLC requis'); return; }
            try { await api.post('/hygiene/dlc', form); toast.success('DLC enregistree'); onSuccess(); onClose(); reset(); }
            catch { toast.error('Erreur'); }
          }}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== PRODUCTION DIALOG =====
function ProductionDialog({ open, onClose, api, mercuriale, today, nowTime, onSuccess }) {
  const [form, setForm] = useState({ date: today, heure: nowTime, plat: '', quantite_produite: '', temperature_finale: '', dlc_produit: '', notes: '' });
  const reset = () => setForm({ date: today, heure: nowTime, plat: '', quantite_produite: '', temperature_finale: '', dlc_produit: '', notes: '' });
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent data-testid="dialog-production">
        <DialogHeader><DialogTitle>Fiche de Production</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Heure</Label><Input type="time" value={form.heure} onChange={e => setForm(f => ({...f, heure: e.target.value}))} /></div>
          </div>
          <div className="space-y-1"><Label>Plat / Preparation</Label><Input value={form.plat} onChange={e => setForm(f => ({...f, plat: e.target.value}))} placeholder="Ex: Sauce ponzu, Base poke" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Quantite</Label><Input value={form.quantite_produite} onChange={e => setForm(f => ({...f, quantite_produite: e.target.value}))} placeholder="5 L" /></div>
            <div className="space-y-1"><Label>Temp. finale (°C)</Label><Input type="number" step="0.1" value={form.temperature_finale} onChange={e => setForm(f => ({...f, temperature_finale: e.target.value}))} /></div>
            <div className="space-y-1"><Label>DLC produit</Label><Input type="date" value={form.dlc_produit} onChange={e => setForm(f => ({...f, dlc_produit: e.target.value}))} /></div>
          </div>
          <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Observations..." /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.plat) { toast.error('Plat requis'); return; }
            try { const payload = { ...form, temperature_finale: form.temperature_finale ? parseFloat(form.temperature_finale) : null }; await api.post('/hygiene/productions', payload); toast.success('Production enregistree'); onSuccess(); onClose(); reset(); }
            catch { toast.error('Erreur'); }
          }}>Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== HUILE DIALOG =====
function HuileDialog({ open, onClose, api, config, today, nowTime, onSuccess }) {
  const initial = { date: today, heure: nowTime, bac: '', type_huile: 'Colza', action: 'controle', polarite: '', aspect: 'normal', couleur: 'claire', temperature: '', quantite_ajoutee: '', notes: '' };
  const [form, setForm] = useState(initial);
  const bacs = config?.huile_bacs || [];
  const reset = () => setForm(initial);
  return (
    <Dialog open={open} onOpenChange={() => { onClose(); reset(); }}>
      <DialogContent className="max-w-lg" data-testid="dialog-huile">
        <DialogHeader><DialogTitle>Controle Huile de Friture</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} /></div>
            <div className="space-y-1"><Label>Heure</Label><Input type="time" value={form.heure} onChange={e => setForm(f => ({...f, heure: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Bac</Label>
              <Select value={form.bac} onValueChange={v => setForm(f => ({...f, bac: v}))}>
                <SelectTrigger data-testid="huile-bac-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{bacs.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Type huile</Label>
              <Select value={form.type_huile} onValueChange={v => setForm(f => ({...f, type_huile: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Colza">Colza</SelectItem>
                  <SelectItem value="Tournesol">Tournesol</SelectItem>
                  <SelectItem value="Arachide">Arachide</SelectItem>
                  <SelectItem value="Olive">Olive</SelectItem>
                  <SelectItem value="Friture">Melange friture</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1"><Label>Action</Label>
            <Select value={form.action} onValueChange={v => setForm(f => ({...f, action: v}))}>
              <SelectTrigger data-testid="huile-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="controle">Controle visuel / polarite</SelectItem>
                <SelectItem value="filtrage">Filtrage</SelectItem>
                <SelectItem value="appoint">Appoint (ajout)</SelectItem>
                <SelectItem value="changement">Changement complet</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Polarite (%)</Label><Input type="number" step="0.1" value={form.polarite} onChange={e => setForm(f => ({...f, polarite: e.target.value}))} placeholder={`max ${config?.huile_polarite_max || 24}`} data-testid="huile-polarite-input" /></div>
            <div className="space-y-1"><Label>Aspect</Label>
              <Select value={form.aspect} onValueChange={v => setForm(f => ({...f, aspect: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="trouble">Trouble</SelectItem>
                  <SelectItem value="mousseux">Mousseux</SelectItem>
                  <SelectItem value="fume">Fume</SelectItem>
                  <SelectItem value="brule">Brule</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Couleur</Label>
              <Select value={form.couleur} onValueChange={v => setForm(f => ({...f, couleur: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="claire">Claire</SelectItem>
                  <SelectItem value="doree">Doree</SelectItem>
                  <SelectItem value="ambree">Ambree</SelectItem>
                  <SelectItem value="foncee">Foncee</SelectItem>
                  <SelectItem value="noire">Noire</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Temperature (°C)</Label><Input type="number" step="0.1" value={form.temperature} onChange={e => setForm(f => ({...f, temperature: e.target.value}))} placeholder="180" /></div>
            <div className="space-y-1"><Label>Qte ajoutee / changee</Label><Input value={form.quantite_ajoutee} onChange={e => setForm(f => ({...f, quantite_ajoutee: e.target.value}))} placeholder="5 L" /></div>
          </div>
          <div className="space-y-1"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Observations..." /></div>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
          <Button onClick={async () => {
            if (!form.bac) { toast.error('Bac requis'); return; }
            try {
              const payload = {
                ...form,
                polarite: form.polarite === '' ? null : parseFloat(form.polarite),
                temperature: form.temperature === '' ? null : parseFloat(form.temperature),
              };
              await api.post('/hygiene/huiles', payload);
              toast.success('Controle enregistre');
              onSuccess(); onClose(); reset();
            } catch { toast.error('Erreur'); }
          }} data-testid="save-huile">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== NETTOYAGE TAB =====
function NettoyageTab({ etat, executions, config, api, onRefresh }) {
  const [newTache, setNewTache] = useState({ titre: '', frequence: 'quotidien', zone: 'Cuisine' });
  const [selectedTache, setSelectedTache] = useState(null);
  const [notes, setNotes] = useState('');

  const addTache = async () => {
    if (!newTache.titre) return;
    try { await api.post('/hygiene/nettoyage/taches', newTache); toast.success('Tache ajoutee'); setNewTache({ titre: '', frequence: 'quotidien', zone: 'Cuisine' }); onRefresh(); }
    catch { toast.error('Erreur'); }
  };

  const validerTache = async (tache_id) => {
    try { await api.post('/hygiene/nettoyage/executions', { tache_id, notes }); toast.success('Valide'); setSelectedTache(null); setNotes(''); onRefresh(); }
    catch { toast.error('Erreur'); }
  };

  const deleteTache = async (id) => {
    if (!window.confirm('Supprimer cette tache ?')) return;
    try { await api.delete(`/hygiene/nettoyage/taches/${id}`); onRefresh(); } catch { toast.error('Erreur'); }
  };

  const byFreq = { quotidien: etat.filter(t => t.frequence === 'quotidien'), hebdomadaire: etat.filter(t => t.frequence === 'hebdomadaire'), mensuel: etat.filter(t => t.frequence === 'mensuel') };

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-3">
        {['quotidien', 'hebdomadaire', 'mensuel'].map(freq => (
          <Card key={freq}>
            <CardHeader className="pb-2"><CardTitle className="text-base capitalize flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" /> {freq} ({byFreq[freq].length})</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {byFreq[freq].length === 0 ? <p className="text-sm text-muted-foreground">Aucune tache</p> :
                byFreq[freq].map(t => (
                  <div key={t.id} className={`flex items-center justify-between gap-2 p-2 rounded border ${t.statut === 'ok' ? 'bg-green-50/30 border-green-200' : t.statut === 'en_retard' ? 'bg-red-50/30 border-red-200' : 'bg-amber-50/30 border-amber-200'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.titre}</p>
                      <p className="text-xs text-muted-foreground">{t.zone} • {t.derniere ? `Fait il y a ${t.jours_depuis}j` : 'Jamais fait'}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7" onClick={() => setSelectedTache(t)} data-testid={`valider-tache-${t.id}`}><CheckCircle className="w-4 h-4 text-green-600" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => deleteTache(t.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Ajouter une tache</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-2">
            <Input placeholder="Titre" value={newTache.titre} onChange={e => setNewTache(p => ({...p, titre: e.target.value}))} data-testid="new-tache-titre" />
            <Select value={newTache.frequence} onValueChange={v => setNewTache(p => ({...p, frequence: v}))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="quotidien">Quotidien</SelectItem><SelectItem value="hebdomadaire">Hebdo</SelectItem><SelectItem value="mensuel">Mensuel</SelectItem></SelectContent>
            </Select>
            <Input placeholder="Zone" value={newTache.zone} onChange={e => setNewTache(p => ({...p, zone: e.target.value}))} />
            <Button onClick={addTache} data-testid="add-tache-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historique des executions</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Tache</TableHead><TableHead>Zone</TableHead><TableHead>Par</TableHead><TableHead>Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {executions.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Aucune execution</TableCell></TableRow> :
                executions.slice(0, 30).map(e => (
                  <TableRow key={e.id}>
                    <TableCell>{e.date} {e.heure}</TableCell>
                    <TableCell className="font-medium">{e.tache_titre}</TableCell>
                    <TableCell>{e.zone}</TableCell>
                    <TableCell className="text-xs">{e.utilisateur}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.notes}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selectedTache} onOpenChange={() => { setSelectedTache(null); setNotes(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Valider : {selectedTache?.titre}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Notes (optionnel)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observations..." />
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Annuler</Button></DialogClose>
            <Button onClick={() => validerTache(selectedTache.id)} data-testid="confirm-valider"><CheckCircle className="w-4 h-4 mr-1" /> Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== AUDIT TAB =====
function AuditTab({ api }) {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [mois, setMois] = useState(currentMonth);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rapport, setRapport] = useState('');
  const [generating, setGenerating] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true); setRapport('');
    try { const res = await api.get(`/hygiene/audit?mois=${mois}`); setAudit(res.data); }
    catch { toast.error('Erreur audit'); }
    finally { setLoading(false); }
  }, [api, mois]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const generateIA = async () => {
    setGenerating(true);
    try { const res = await api.post('/hygiene/audit/analyse-ia', { audit }); setRapport(res.data.rapport); }
    catch { toast.error('Erreur IA'); }
    finally { setGenerating(false); }
  };

  const exportPDF = () => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/hygiene/audit-pdf?mois=${mois}&token=${token}`, '_blank');
  };

  if (loading || !audit) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const colorBg = { green: 'bg-green-50 border-green-300', blue: 'bg-blue-50 border-blue-300', amber: 'bg-amber-50 border-amber-300', red: 'bg-red-50 border-red-300' };
  const colorText = { green: 'text-green-600', blue: 'text-blue-600', amber: 'text-amber-600', red: 'text-red-600' };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={mois} onChange={e => setMois(e.target.value)} className="w-40" data-testid="audit-mois-input" />
        <Button onClick={fetchAudit} variant="outline" size="sm">Recalculer</Button>
        <Button onClick={exportPDF} variant="outline" size="sm"><FileDown className="w-4 h-4 mr-1" /> Export PDF</Button>
        <Button onClick={generateIA} disabled={generating} size="sm" data-testid="audit-ia-btn"><Sparkles className="w-4 h-4 mr-1" /> {generating ? 'Analyse IA...' : 'Analyse IA'}</Button>
      </div>

      <Card className={`border-2 ${colorBg[audit.couleur]}`}>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-muted-foreground">Score HACCP — {audit.mois}</p>
          <p className={`text-6xl font-bold tabular-nums ${colorText[audit.couleur]}`}>{audit.score_total}<span className="text-2xl">/100</span></p>
          <p className={`text-lg font-semibold mt-2 ${colorText[audit.couleur]}`}>{audit.niveau}</p>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        {audit.details.map((d, i) => {
          const pct = d.max > 0 ? (d.score / d.max) * 100 : 0;
          const c = pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-blue-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600';
          return (
            <Card key={i}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-sm">{d.critere}</p>
                  <p className={`text-xl font-bold tabular-nums ${c}`}>{d.score}<span className="text-sm text-muted-foreground">/{d.max}</span></p>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-2">
                  {Object.entries(d.stats || {}).map(([k, v]) => <span key={k}>{k}: <b>{v}</b></span>)}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {rapport && (
        <Card className="border-2 border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-600" /> Analyse IA</CardTitle></CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap" data-testid="audit-ia-rapport">{rapport}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== FORMATION TAB =====
function FormationTab({ api, config, qcmList, resultats, onRefresh }) {
  const [genMode, setGenMode] = useState({ regle_titre: '', nb_questions: 5 });
  const [generating, setGenerating] = useState(false);
  const [currentQcm, setCurrentQcm] = useState(null);
  const [participant, setParticipant] = useState('');
  const [reponses, setReponses] = useState({});
  const [resultCurrent, setResultCurrent] = useState(null);
  const regles = config?.regles_haccp || [];

  const generate = async () => {
    setGenerating(true);
    try {
      const payload = { ...genMode };
      if (payload.regle_titre === '__all__') payload.regle_titre = '';
      await api.post('/hygiene/formation/generer-qcm', payload);
      toast.success('QCM genere');
      onRefresh();
    }
    catch { toast.error('Erreur IA'); }
    finally { setGenerating(false); }
  };

  const deleteQcm = async (id) => {
    if (!window.confirm('Supprimer ce QCM ?')) return;
    try { await api.delete(`/hygiene/formation/qcm/${id}`); onRefresh(); } catch { toast.error('Erreur'); }
  };

  const startQcm = (qcm) => { setCurrentQcm(qcm); setReponses({}); setResultCurrent(null); setParticipant(''); };

  const submit = async () => {
    if (!participant.trim()) { toast.error('Nom du participant requis'); return; }
    if (Object.keys(reponses).length < currentQcm.questions.length) { toast.error('Reponds a toutes les questions'); return; }
    try {
      const res = await api.post('/hygiene/formation/submit', { qcm_id: currentQcm.id, reponses, participant });
      setResultCurrent(res.data);
      onRefresh();
    } catch { toast.error('Erreur'); }
  };

  const downloadCert = (result_id) => {
    const token = localStorage.getItem('token');
    window.open(`${API}/api/hygiene/formation/certificat/${result_id}?token=${token}`, '_blank');
  };

  // Pass rate per participant
  const statsParticipants = {};
  resultats.forEach(r => {
    if (!statsParticipants[r.participant]) statsParticipants[r.participant] = { total: 0, reussi: 0, dernier_score: 0 };
    statsParticipants[r.participant].total += 1;
    if (r.reussi) statsParticipants[r.participant].reussi += 1;
    statsParticipants[r.participant].dernier_score = r.score;
  });

  return (
    <div className="space-y-4">
      {/* Generate */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> Generer un QCM IA</CardTitle>
          <CardDescription>Base sur une regle HACCP — 5 questions + certificat si score ≥ 70%</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px] space-y-1"><Label className="text-xs">Regle HACCP</Label>
              <Select value={genMode.regle_titre} onValueChange={v => setGenMode(p => ({...p, regle_titre: v}))}>
                <SelectTrigger data-testid="qcm-regle-select"><SelectValue placeholder="Toutes les regles" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Toutes les regles</SelectItem>
                  {regles.map((r, i) => <SelectItem key={i} value={r.titre}>{r.titre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-24 space-y-1"><Label className="text-xs">Questions</Label><Input type="number" value={genMode.nb_questions} onChange={e => setGenMode(p => ({...p, nb_questions: parseInt(e.target.value) || 5}))} /></div>
            <Button onClick={() => generate()} disabled={generating} data-testid="generate-qcm-btn"><Sparkles className="w-4 h-4 mr-1" /> {generating ? 'Generation...' : 'Generer'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* QCM list */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">QCM disponibles ({qcmList.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {qcmList.length === 0 ? <p className="text-sm text-muted-foreground">Aucun QCM. Genere ton premier ci-dessus.</p> :
            qcmList.map(q => (
              <div key={q.id} className="flex items-center justify-between p-3 rounded border">
                <div>
                  <p className="font-medium text-sm">{q.regle_titre}</p>
                  <p className="text-xs text-muted-foreground">{q.questions.length} questions • {new Date(q.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" onClick={() => startQcm(q)} data-testid={`start-qcm-${q.id}`}>Faire le QCM</Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteQcm(q.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historique des formations</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Participant</TableHead><TableHead>Module</TableHead>
              <TableHead className="text-right">Score</TableHead><TableHead>Resultat</TableHead><TableHead className="w-[90px]"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {resultats.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Aucun resultat</TableCell></TableRow> :
                resultats.slice(0, 30).map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.date}</TableCell>
                    <TableCell className="font-medium">{r.participant}</TableCell>
                    <TableCell>{r.regle_titre}</TableCell>
                    <TableCell className={`text-right font-bold tabular-nums ${r.reussi ? 'text-green-600' : 'text-red-600'}`}>{r.score}%</TableCell>
                    <TableCell>{r.reussi ? <Badge className="bg-green-100 text-green-700">Valide</Badge> : <Badge className="bg-red-100 text-red-700">Echec</Badge>}</TableCell>
                    <TableCell>
                      {r.reussi && <Button size="sm" variant="ghost" onClick={() => downloadCert(r.id)} title="Telecharger certificat"><Award className="w-4 h-4 text-amber-600" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* QCM Dialog */}
      <Dialog open={!!currentQcm} onOpenChange={() => { setCurrentQcm(null); setResultCurrent(null); setReponses({}); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{currentQcm?.regle_titre}</DialogTitle></DialogHeader>
          {!resultCurrent ? (
            <div className="space-y-4">
              <div className="space-y-1"><Label>Nom du participant</Label>
                <Input value={participant} onChange={e => setParticipant(e.target.value)} placeholder="Prenom Nom" data-testid="qcm-participant-input" />
              </div>
              {currentQcm?.questions?.map((q, i) => (
                <Card key={i}><CardContent className="pt-4 pb-3">
                  <p className="font-semibold text-sm mb-2">Q{i+1}. {q.question}</p>
                  <div className="space-y-1">
                    {Object.entries(q.choix || {}).map(([key, val]) => (
                      <label key={key} className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-sm ${reponses[i] === key ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}`}>
                        <input type="radio" name={`q${i}`} checked={reponses[i] === key} onChange={() => setReponses(p => ({...p, [i]: key}))} />
                        <span><b>{key}.</b> {val}</span>
                      </label>
                    ))}
                  </div>
                </CardContent></Card>
              ))}
              <Button onClick={submit} className="w-full" data-testid="submit-qcm-btn">Soumettre le QCM</Button>
            </div>
          ) : (
            <div className="space-y-3 text-center py-4">
              <div className={`text-6xl font-bold ${resultCurrent.reussi ? 'text-green-600' : 'text-red-600'}`}>{resultCurrent.score}%</div>
              <p className="text-lg">{resultCurrent.correct}/{resultCurrent.total} bonnes reponses</p>
              {resultCurrent.reussi ? (
                <>
                  <Badge className="bg-green-100 text-green-700 text-base px-4 py-1">Formation validee</Badge>
                  <Button onClick={() => downloadCert(resultCurrent.id)} className="mt-2"><Award className="w-4 h-4 mr-1" /> Telecharger le certificat</Button>
                </>
              ) : (
                <>
                  <Badge className="bg-red-100 text-red-700 text-base px-4 py-1">Score insuffisant (min 70%)</Badge>
                  <p className="text-sm text-muted-foreground">Revoyez les regles et reessayez.</p>
                </>
              )}
              <div className="text-left text-xs space-y-1 pt-3 border-t">
                {resultCurrent.detail.map((d, i) => (
                  <div key={i} className={`p-2 rounded ${d.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                    <b>Q{i+1}: {d.correct ? '✓' : '✗'}</b> {d.question}
                    {!d.correct && <p className="text-muted-foreground mt-1">Bonne reponse: <b>{d.reponse_correcte}</b> — {d.explication}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== CONFIG PANEL =====
function ConfigPanel({ config, api, onSave }) {
  const [c, setC] = useState(config);
  const [newProd, setNewProd] = useState({ nom: '', type: 'frais', temp_max: '4' });
  const [newBac, setNewBac] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (update) => {
    setSaving(true);
    try { await api.put('/hygiene/config', update || c); toast.success('Config sauvegardee'); onSave(); }
    catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Seuils de temperature</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1"><Label className="text-xs">Frigo positif min (°C)</Label><Input type="number" step="0.1" value={c.temp_frigo_min} onChange={e => setC(p => ({...p, temp_frigo_min: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Frigo positif max (°C)</Label><Input type="number" step="0.1" value={c.temp_frigo_max} onChange={e => setC(p => ({...p, temp_frigo_max: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Saladette min (°C)</Label><Input type="number" step="0.1" value={c.temp_saladette_min ?? 0} onChange={e => setC(p => ({...p, temp_saladette_min: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Saladette max (°C)</Label><Input type="number" step="0.1" value={c.temp_saladette_max ?? 4} onChange={e => setC(p => ({...p, temp_saladette_max: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Congelateur min</Label><Input type="number" step="0.1" value={c.temp_congelateur_min} onChange={e => setC(p => ({...p, temp_congelateur_min: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Congelateur max</Label><Input type="number" step="0.1" value={c.temp_congelateur_max} onChange={e => setC(p => ({...p, temp_congelateur_max: parseFloat(e.target.value)}))} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Reception frais max</Label><Input type="number" step="0.1" value={c.temp_reception_frais_max} onChange={e => setC(p => ({...p, temp_reception_frais_max: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Reception congele max</Label><Input type="number" step="0.1" value={c.temp_reception_congele_max} onChange={e => setC(p => ({...p, temp_reception_congele_max: parseFloat(e.target.value)}))} /></div>
            <div className="space-y-1"><Label className="text-xs">Alerte DLC (jours avant)</Label><Input type="number" value={c.alerte_dlc_jours} onChange={e => setC(p => ({...p, alerte_dlc_jours: parseInt(e.target.value)}))} /></div>
          </div>
          <Button onClick={() => save()} disabled={saving} size="sm">{saving ? 'Sauvegarde...' : 'Sauvegarder les seuils'}</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Produits a suivre en temperature</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(c.produits_a_suivre || []).map((p, i) => (
              <div key={i} className="flex items-center gap-1 px-3 py-1.5 bg-muted/50 rounded-full border text-sm">
                {p.nom} <span className="text-xs text-muted-foreground">(max {p.temp_max}°C)</span>
                <button onClick={() => { const next = [...c.produits_a_suivre]; next.splice(i, 1); const updated = {...c, produits_a_suivre: next}; setC(updated); save(updated); }}
                  className="ml-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2 pt-2 border-t">
            <div className="flex-1 space-y-1"><Label className="text-xs">Produit</Label><Input value={newProd.nom} onChange={e => setNewProd(p => ({...p, nom: e.target.value}))} placeholder="Ex: Thon" /></div>
            <div className="w-24 space-y-1"><Label className="text-xs">Temp. max</Label><Input type="number" value={newProd.temp_max} onChange={e => setNewProd(p => ({...p, temp_max: e.target.value}))} /></div>
            <Button size="sm" onClick={() => {
              if (!newProd.nom) return;
              const next = [...(c.produits_a_suivre || []), { nom: newProd.nom, type: newProd.type, temp_max: parseFloat(newProd.temp_max) || 4 }];
              const updated = {...c, produits_a_suivre: next};
              setC(updated); save(updated); setNewProd({ nom: '', type: 'frais', temp_max: '4' });
            }}><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Droplets className="w-4 h-4 text-amber-600" /> Huiles de friture</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Polarite max (%)</Label><Input type="number" step="0.1" value={c.huile_polarite_max ?? 24} onChange={e => setC(p => ({...p, huile_polarite_max: parseFloat(e.target.value)}))} data-testid="huile-polarite-max-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Frequence changement (jours)</Label><Input type="number" value={c.huile_changement_jours ?? 7} onChange={e => setC(p => ({...p, huile_changement_jours: parseInt(e.target.value)}))} /></div>
          </div>
          <Button onClick={() => save()} disabled={saving} size="sm">{saving ? 'Sauvegarde...' : 'Sauvegarder seuils huile'}</Button>
          <Separator />
          <Label className="font-semibold text-sm">Bacs / Friteuses</Label>
          <div className="flex flex-wrap gap-2">
            {(c.huile_bacs || []).map((b, i) => (
              <div key={i} className="flex items-center gap-1 px-3 py-1.5 bg-muted/50 rounded-full border text-sm">
                {b}
                <button onClick={() => { const next = [...(c.huile_bacs || [])]; next.splice(i, 1); const updated = {...c, huile_bacs: next}; setC(updated); save(updated); }}
                  className="ml-1 text-muted-foreground hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2 pt-2 border-t">
            <div className="flex-1 space-y-1"><Label className="text-xs">Nouveau bac</Label><Input value={newBac} onChange={e => setNewBac(e.target.value)} placeholder="Ex: Friteuse 3" data-testid="new-bac-input" /></div>
            <Button size="sm" onClick={() => {
              if (!newBac.trim()) return;
              const next = [...(c.huile_bacs || []), newBac.trim()];
              const updated = {...c, huile_bacs: next};
              setC(updated); save(updated); setNewBac('');
            }} data-testid="add-bac-btn"><Plus className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
